/**
 * Le contrat de la gouvernance tarifaire, côté navigateur (`EX-TAR-024`,
 * `EX-TAR-026`, `EX-TAR-027`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `PricingGovernanceMessages`, sans rien y
 * ajouter. Un contrôle confronte champ par champ cette déclaration au fichier
 * Java lui-même.
 *
 * ## Ce que ce chantier referme
 *
 * `PricingGovernanceApi` servait **sept routes, éprouvées, et appelées par
 * aucun écran** — c'est le module qui en portait le plus de toute la famille
 * `D-25` / `D-30` / `D-35` / `D-45` / `D-57` :
 *
 *  - les plans et leurs versions ;
 *  - l'historique d'activation d'un plan ;
 *  - la comparaison de deux versions, règle par règle ;
 *  - la simulation d'un tarif sur une opération imaginaire ;
 *  - le **rejeu** d'une version candidate sur le trafic réel passé ;
 *  - les écarts du **mode fantôme** ;
 *  - la rentabilité par segment et par guichet.
 *
 * ## Trois gestes sur cinq, et les deux absents sont déclarés
 *
 * Le socle exécute cinq actions. Trois portent sur un objet que ces routes
 * **listent** — un plan, une version : brouillon, activation, retour arrière.
 * Les deux autres portent sur une **promotion**, et *aucune route ne liste les
 * promotions*. Les offrir exigerait de connaître une référence par avance,
 * c'est-à-dire la moitié d'une interface — le défaut que le module Clients a
 * payé, et que la liquidité a payé une seconde fois dans ce même chantier. Ils
 * sont donc absents et écrits dans `deferred-work.md`.
 *
 * ## `servedByReplica` est rendu, et il n'est pas décoratif
 *
 * Un rejeu parcourt le trafic réel : c'est la requête la plus lourde de tout le
 * back-office. Savoir si elle a été servie par la réplique ou par la base
 * principale est une information d'exploitation — sur la principale, elle prend
 * des connexions à ce qui sert les retraits.
 */

import type { ApiClient } from './api-client.js';

/** La racine des routes de gouvernance tarifaire. */
export const pricingPath = '/api/backoffice/pricing';

/** Le chemin de la route unique des actions habilitables. */
export const pricingActionsPath = '/api/backoffice/actions';

/** Le motif écrit au journal pour toute lecture de ce module. */
export const pricingReason = 'gouvernance-tarifaire';

/** Les trois gestes de cycle de vie d'une version. */
export const pricingActions = {
  /** Ouvrir un brouillon de version. */
  draft: 'TARIFF_UPDATE',
  /** Mettre une version en vigueur, en fantôme, ou en progressif. */
  activate: 'PRICING_PLAN_ACTIVATE',
  /** Revenir à la version précédente. */
  rollback: 'PRICING_VERSION_ROLLBACK',
} as const;

/**
 * Les trois modes d'activation, tels qu'`ActivationMode` les nomme.
 *
 * Liste **fermée**, défendue par le serveur. Un contrôle relit l'énumération
 * Java et la confronte à celle-ci : un quatrième mode ajouté côté serveur
 * rendrait cette table fausse en silence.
 */
export const activationModes = ['ACTIF', 'FANTOME', 'PROGRESSIF'] as const;

/** Un mode d'activation. */
export type ActivationMode = (typeof activationModes)[number];

/** Une version d'un plan. Suit `PricingGovernanceMessages.VersionView`. */
export interface PlanVersion {
  readonly id: string;
  readonly versionNo: number;
  readonly status: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly rolloutPercentage: number;
  readonly grandfatherFrom: string | null;
  readonly createdBy: string | null;
  readonly approvedBy: string | null;
  readonly changeReason: string | null;
}

/** Un plan tarifaire et ses versions. Suit `PlanView`. */
export interface PricingPlan {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly scope: string;
  readonly isDefault: boolean;
  readonly versions: readonly PlanVersion[];
}

/**
 * Une entrée d'historique d'activation. Suit `ActivationView`.
 *
 * `approvedBy` peut manquer : une activation qui n'a pas encore reçu sa seconde
 * signature n'a pas de second signataire. Le rendre égal à `requestedBy` ferait
 * croire qu'une personne a signé deux fois — ce que la double validation existe
 * précisément pour empêcher.
 */
export interface PlanActivation {
  readonly id: string;
  readonly planVersionId: string;
  readonly previousVersionId: string | null;
  readonly operation: string;
  readonly mode: string | null;
  readonly rolloutPercentage: number | null;
  readonly grandfatherFrom: string | null;
  readonly requestedBy: string | null;
  readonly approvedBy: string | null;
  readonly reason: string | null;
  readonly occurredAt: string;
}

/** Une règle tarifaire. Suit `RuleView`. */
export interface PricingRule {
  readonly id: string;
  readonly billableEvent: string;
  readonly priority: number;
  readonly calcType: string;
  readonly fixedAmount: number | null;
  readonly percentBp: number | null;
  readonly minAmount: number | null;
  readonly maxAmount: number | null;
  readonly tierMode: string | null;
  readonly roundingMode: string | null;
  readonly payer: string;
  readonly active: boolean;
}

/**
 * La comparaison d'une règle entre deux versions. Suit `RuleComparisonView`.
 *
 * `left` ou `right` manque quand la règle n'existe que d'un côté — c'est
 * précisément ce qu'une comparaison sert à montrer, et le rendre par une règle
 * vide ferait lire « une règle à zéro franc » là où il faut lire « aucune
 * règle ».
 */
export interface RuleComparison {
  readonly matchKey: string;
  readonly verdict: string;
  readonly left: PricingRule | null;
  readonly right: PricingRule | null;
}

/**
 * Le résultat d'une simulation. Suit `SimulationView`.
 *
 * `trace` est la **suite des règles examinées**, dans l'ordre. C'est ce qui
 * distingue une simulation d'un chiffre : un administrateur tarifaire ne
 * demande pas seulement combien, il demande **pourquoi**.
 */
export interface PricingSimulation {
  readonly billableEvent: string;
  readonly baseAmount: number;
  readonly feeAmount: number;
  readonly planVersionId: string | null;
  readonly ruleId: string | null;
  readonly payer: string;
  readonly trace: readonly string[];
  readonly servedByReplica: boolean;
}

/** Un segment du rejeu. Suit `ReplaySegmentView`. */
export interface ReplaySegment {
  readonly segmentCode: string;
  readonly operations: number;
  readonly priced: number;
  readonly unpriced: number;
  readonly appliedRevenue: number;
  readonly candidateRevenue: number;
  readonly deltaRevenue: number;
}

/**
 * Le rejeu d'une version candidate sur le trafic passé. Suit `ReplayView`.
 *
 * **`unpriced` ne se tait jamais.** Une opération que la version candidate ne
 * sait pas tarifer n'est pas une opération à zéro franc : c'est un trou dans la
 * grille. Un delta de revenu calculé en ignorant ces opérations serait un
 * chiffre rassurant tiré d'une grille incomplète.
 */
export interface PricingReplay {
  readonly candidateVersionId: string;
  readonly from: string;
  readonly to: string;
  readonly examined: number;
  readonly appliedRevenue: number;
  readonly candidateRevenue: number;
  readonly deltaRevenue: number;
  readonly unpriced: number;
  readonly bySegment: readonly ReplaySegment[];
  readonly servedByReplica: boolean;
}

/** Un segment du mode fantôme. Suit `ShadowSegmentView`. */
export interface ShadowSegment {
  readonly segmentCode: string;
  readonly observed: number;
  readonly priced: number;
  readonly unpriced: number;
  readonly appliedTotal: number;
  readonly shadowTotal: number;
  readonly deltaFrancs: number;
}

/** Les écarts observés en mode fantôme. Suit `ShadowView`. */
export interface ShadowObservation {
  readonly planVersionId: string;
  readonly from: string;
  readonly to: string;
  readonly bySegment: readonly ShadowSegment[];
}

/** Une ligne de rentabilité. Suit `ProfitabilityRowView`. */
export interface ProfitabilityRow {
  readonly key: string;
  readonly label: string;
  readonly operations: number;
  readonly revenue: number;
  readonly distributionCost: number;
  readonly promotionCost: number;
  readonly netMargin: number;
  readonly servedByReplica: boolean;
}

/** La rentabilité par segment et par guichet. Suit `ProfitabilityView`. */
export interface Profitability {
  readonly from: string;
  readonly to: string;
  readonly bySegment: readonly ProfitabilityRow[];
  readonly byOutlet: readonly ProfitabilityRow[];
}

/** Ce que rend la route des actions. */
export interface ActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/** Ce qu'une simulation demande. Seuls les trois premiers sont exigés. */
export interface SimulationQuery {
  readonly accountId: string;
  readonly billableEvent: string;
  readonly amount: number;
  readonly channel?: string;
  readonly kycLevel?: string;
  readonly holderType?: string;
  readonly outletRegion?: string;
  readonly promoCode?: string;
  readonly at?: string;
}

/** Lit les plans tarifaires et leurs versions. */
export async function fetchPricingPlans(
  client: ApiClient,
  reason: string = pricingReason,
): Promise<readonly PricingPlan[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly PricingPlan[]>(
    `${pricingPath}/plans?${parametres.toString()}`,
  );
}

/** Lit l'historique d'activation d'un plan. */
export async function fetchPlanActivations(
  client: ApiClient,
  planId: string,
  reason: string = pricingReason,
): Promise<readonly PlanActivation[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly PlanActivation[]>(
    `${pricingPath}/plans/${encodeURIComponent(planId)}/activations?${parametres.toString()}`,
  );
}

/** Compare deux versions, règle par règle. */
export async function comparePlanVersions(
  client: ApiClient,
  left: string,
  right: string,
  reason: string = pricingReason,
): Promise<readonly RuleComparison[]> {
  const parametres = new URLSearchParams({ left, right, reason });
  return client.request<readonly RuleComparison[]>(
    `${pricingPath}/compare?${parametres.toString()}`,
  );
}

/**
 * Simule un tarif sur une opération imaginaire.
 *
 * Aucune capture d'erreur : rendre un tarif nul ferait confondre « cette
 * opération est gratuite » — un fait — avec « le serveur a refusé », qui n'en
 * est pas un. Sur l'écran où l'on décide d'une grille, la confusion se lit
 * comme un tarif.
 */
export async function simulatePricing(
  client: ApiClient,
  query: SimulationQuery,
  reason: string = pricingReason,
): Promise<PricingSimulation> {
  const parametres = new URLSearchParams({
    accountId: query.accountId,
    billableEvent: query.billableEvent,
    amount: String(query.amount),
    reason,
  });
  // Un critère vide n'est PAS envoyé vide : `channel=` serait un critère que le
  // serveur devrait interpréter, là où son absence dit « aucun ».
  const poser = (nom: string, valeur: string | undefined): void => {
    if (valeur !== undefined && valeur !== '') parametres.set(nom, valeur);
  };
  poser('channel', query.channel);
  poser('kycLevel', query.kycLevel);
  poser('holderType', query.holderType);
  poser('outletRegion', query.outletRegion);
  poser('promoCode', query.promoCode);
  poser('at', query.at);
  return client.request<PricingSimulation>(
    `${pricingPath}/simulate?${parametres.toString()}`,
  );
}

/** Rejoue une version candidate sur le trafic réel passé. */
export async function replayPricing(
  client: ApiClient,
  version: string,
  days?: number,
  reason: string = pricingReason,
): Promise<PricingReplay> {
  const parametres = new URLSearchParams({ version, reason });
  if (days !== undefined) parametres.set('days', String(days));
  return client.request<PricingReplay>(`${pricingPath}/replay?${parametres.toString()}`);
}

/** Lit les écarts observés en mode fantôme pour une version. */
export async function fetchShadowObservation(
  client: ApiClient,
  versionId: string,
  days?: number,
  reason: string = pricingReason,
): Promise<ShadowObservation> {
  const parametres = new URLSearchParams({ reason });
  if (days !== undefined) parametres.set('days', String(days));
  return client.request<ShadowObservation>(
    `${pricingPath}/shadow/${encodeURIComponent(versionId)}?${parametres.toString()}`,
  );
}

/** Lit la rentabilité par segment et par guichet. */
export async function fetchProfitability(
  client: ApiClient,
  days?: number,
  reason: string = pricingReason,
): Promise<Profitability> {
  const parametres = new URLSearchParams({ reason });
  if (days !== undefined) parametres.set('days', String(days));
  return client.request<Profitability>(
    `${pricingPath}/profitability?${parametres.toString()}`,
  );
}

/**
 * Demande un geste de cycle de vie sur un plan ou une version.
 *
 * **Le motif est saisi, geste par geste.** Activer une grille engage les tarifs
 * de tout le portefeuille : la raison de CE changement-là est ce que le journal
 * doit porter, et elle n'est jamais « gouvernance tarifaire ».
 *
 * `PRICING_PLAN_ACTIVATE` et `PRICING_PROMOTION_CREATE` sont en **double
 * validation** dans la matrice : le socle rend alors `202` avec l'identifiant
 * de la demande, et l'écran ne doit pas annoncer un tarif en vigueur. C'est ce
 * que `outcome` distingue, et l'écran le rend tel quel.
 */
export async function performPricingAction(
  client: ApiClient,
  demande: {
    readonly action: string;
    readonly entityRef: string;
    readonly reason: string;
    readonly payload?: Readonly<Record<string, string>>;
  },
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(
    `${pricingActionsPath}/${demande.action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.entityRef,
        reason: demande.reason,
        payload: demande.payload ?? {},
      }),
    },
  );
}
